import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { GridRClickDirective } from '@app/shared/directives/grid-r-click/grid-r-click.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { IItemCostCodeCostTypeDto } from '@app/shared/models/dtos/item-cost-code-cost-type';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogComponent } from '@progress/kendo-angular-dialog';
import { DataStateChangeEvent, GridDataResult, GridModule, RowArgs, SelectionEvent, } from '@progress/kendo-angular-grid';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { orderBy, SortDescriptor, State } from '@progress/kendo-data-query';
import { map, Observable } from 'rxjs';
import { CostCodeCostTypeProjectCombination } from '../../../../cem/models/cost-code-cost-type-project-combination';
import { IKeyedPhaseStepSort } from '../../estimate/project-estimate-detail/project-estimate-detail.component';
import { GridState, GridStateSort, ItemGridService } from '../../services/item-grid.service';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';

@Component({
  selector: 'app-project-item-select',
  templateUrl: './project-item-select.component.html',
  styleUrls: ['./project-item-select.component.css'],
  imports: [
    AsyncPipe,
    ButtonDirective,
    DialogComponent,
    GridModule,
    GridRClickDirective,
    LoaderComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    ItemGridService,
  ],
})
export class ProjectItemSelectComponent implements OnInit {
  public itemGridService = inject(ItemGridService);
  private lookupPlusService = inject(LookupPlusService);

  public dCCCTCombination = Array<CostCodeCostTypeProjectCombination>();
  private tCCCTPCombination: string = 'CostCodeCostTypeProjectCombinations';
  private fCCCTPCombination: string = '$filter=ProjectTypeId%20eq%20';
  private qCCCTPCombination: string;

  public icons = kendoIcons;
  public pageSize: number = 100;
  public view: Observable<GridDataResult>;
  public gridView: GridDataResult = { data: [], total: 0 };
  public stateCustom: GridState = new GridState(this.pageSize, 1);
  public state: State = { skip: 0, take: this.pageSize };
  public projectType: number;
  public selectedItems: RowArgs[] = [];
  public selectedPhaseStepKey: string[] = [];
  public phaseStepSort: SortDescriptor[] = [];

  readonly iProjectTypeId = input<number>(undefined);
  readonly showDialog = input<boolean>(undefined);
  readonly showPhaseStep = input<boolean>(undefined);
  readonly iDistinctPhaseStep = input<Array<IKeyedPhaseStepSort>>([]);

  readonly addItem = output<{
    items: IItemCostCodeCostTypeDto[];
    phaseSteps: string[];
}>();
  readonly closeDialog = output<any>();

  ngOnInit() {
    this.syncStateCustomFromState();
    this.projectType = this.iProjectTypeId();
    this.qCCCTPCombination = `${this.fCCCTPCombination}${this.iProjectTypeId()}`;
    this.getdCCCTCombination(this.tCCCTPCombination, this.qCCCTPCombination)
      .then(() => {
        this.itemGridService.query(this.stateCustom);
      });

    this.view = this.itemGridService
      .pipe(
        map(x => {
          if (!x) {
            return this.gridView;
          }

          const filteredData = {
            data: x.data.filter(item => this.checkCCCTPCombination(item.CostCodeCostTypeId)),
            total: x.total
          };
          this.gridView = filteredData;
          return filteredData;

        }),
      )
  }

  private getdCCCTCombination(table: string, query: string): Promise<void> {
    return this.lookupPlusService.getLookup(table, query)
      .then(payload => {
        this.dCCCTCombination = payload.value;
      });
  }

  public checkCCCTPCombination(ccctId: number): boolean {
    // check the ccct Id of the selected item to see if it is valid for this project Type
    // This method returns true if valid, false if invalid
    return this.dCCCTCombination.findIndex(x => x.CostCodeCostTypeId === ccctId) !== -1 ? true : false;
  }

  public dataStateChange(state: DataStateChangeEvent): void {
    this.state = state;
    this.syncStateCustomFromState();

    if (this.state.sort?.length && !!this.state.sort[0].dir) {
      this.stateCustom.sort = this.state.sort.map(x => new GridStateSort(x.field, x.dir));
    } else {
      this.stateCustom.sort = [];
    }

    this.itemGridService.query(this.stateCustom);
  }

  private syncStateCustomFromState(): void {
    this.stateCustom.projectTypeId = this.iProjectTypeId();
    if (this.state.skip !== null) {
      this.stateCustom.pageNumber = this.state.skip / this.pageSize + 1;
    } else {
      this.stateCustom.pageNumber = 1;
    }

    if (this.state.filter) {
      this.stateCustom = Object.assign(this.stateCustom, this.state);
    }
  }

  public phaseStepSortChange(sort: SortDescriptor[]): void {
    this.phaseStepSort = sort;
    this.iDistinctPhaseStep().splice(0, this.iDistinctPhaseStep().length, ...orderBy(this.iDistinctPhaseStep(), sort));
  }

  public phaseSelectionChange(event: SelectionEvent): void {
    event.selectedRows.forEach((row) => {
      const key = row.dataItem?.PSKey as string;
      if (key && !this.selectedPhaseStepKey.includes(key)) {
        this.selectedPhaseStepKey.push(key);
      }
    });

    event.deselectedRows.forEach((row) => {
      const key = row.dataItem?.PSKey as string;
      this.selectedPhaseStepKey = this.selectedPhaseStepKey.filter(k => k !== key);
    });
  }

  public selectionChange(event: SelectionEvent): void {
    event.selectedRows.forEach((row) => {
      this.selectedItems.push(row);
    });

    event.deselectedRows.forEach((row) => {
      this.selectedItems = this.selectedItems.filter(f => f.index !== row.index);
    });
  }

  public addSelected(): void {
    let items = this.selectedItems.map(m => m.dataItem);
    let phaseSteps = this.selectedPhaseStepKey;
    this.addItem.emit({ items, phaseSteps });

    this.clearSelections();
  }

  public disableAdd(): boolean {
    if (this.showPhaseStep()) {
      return (this.selectedItems.length === 0 || this.selectedPhaseStepKey.length === 0);
    }
    else {
      return (this.selectedItems.length === 0);
    }
  }

  public close(): void {
    this.clearSelections();
    this.closeDialog.emit(null);
  }

  private clearSelections(): void {
    this.selectedItems = [];
    this.selectedPhaseStepKey = [];
  }
}